import * as Sentry from '@sentry/nextjs';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import {
  getMediaElementSource,
  setMediaElementSource,
} from '@/features/AudioPlayer/atoms/playback/webAudioGraph';
import { log } from '@/lib/utils';

import {
  initialAudioPlayerState,
  initialErrorState,
  initialPlaybackState,
} from './constants';

import type {
  AudioPlayerError,
  AudioPlayerState,
} from '@/features/AudioPlayer/types';

/**
 * PRIMARY ATOMS
 * These atoms store the base state and are typically not accessed directly by components.
 * They serve as the foundation for derived atoms and actions.
 */

// Track the last search queries to detect changes
export const lastMusicQueryAtom = atom<string | null>(null);
lastMusicQueryAtom.debugLabel = 'lastMusicQueryAtom';

export const lastMusicFiltersAtom = atom<string | null>(null);
lastMusicFiltersAtom.debugLabel = 'lastMusicFiltersAtom';

export const lastSfxQueryAtom = atom<string | null>(null);
lastSfxQueryAtom.debugLabel = 'lastSfxQueryAtom';

export const lastSfxFiltersAtom = atom<string | null>(null);
lastSfxFiltersAtom.debugLabel = 'lastSfxFiltersAtom';

/**
 * Main state atom for the entire audio player
 * Contains both the current queue and loaded queues
 * Acts as the central source of truth for audio player state
 */
export const audioPlayerStateAtom = atom<AudioPlayerState>(
  initialAudioPlayerState
);
audioPlayerStateAtom.debugLabel = 'audioPlayerStateAtom';

/**
 * Store the playback state - holds current playback status, time, duration
 * This includes isPlaying, currentTime, duration, buffered, volume, and muted state
 */
export const playbackAtom =
  atom<typeof initialPlaybackState>(initialPlaybackState);
playbackAtom.debugLabel = 'playbackAtom';

/**
 * Store the audio element reference - gives access to the HTML Audio element
 * This allows direct control of the native audio APIs
 */
export const audioElementAtom = atom<HTMLAudioElement | null>(null);
audioElementAtom.debugLabel = 'audioElementAtom';

/**
 * Store the AudioContext reference for the Web Audio graph.
 * Lifetime: created lazily on first play (or first user gesture on Safari) and
 * persists for the page lifetime — survives resetAudioPlayerAtom.
 */
export const audioContextAtom = atom<AudioContext | null>(null);
audioContextAtom.debugLabel = 'audioContextAtom';

/**
 * Store the GainNode reference. Volume/mute writes go to gainNode.gain.value
 * once the graph exists. Survives resets.
 */
export const gainNodeAtom = atom<GainNode | null>(null);
gainNodeAtom.debugLabel = 'gainNodeAtom';

/**
 * Store volume preference in localStorage - persists between sessions
 * Uses atomWithStorage to automatically save/restore from localStorage
 */
export const volumePreferenceAtom = atomWithStorage<number>(
  'audioPlayerVolume',
  1 // Default to full volume
);
volumePreferenceAtom.debugLabel = 'volumePreferenceAtom';

/**
 * Store mute preference in localStorage - persists between sessions
 * Remembers if user has muted the player between page loads
 */
export const mutePreferenceAtom = atomWithStorage<boolean>(
  'audioPlayerMuted',
  false // Default to not muted
);
mutePreferenceAtom.debugLabel = 'mutePreferenceAtom';

/**
 * Store error state for the audio player - used to display errors to the user
 * and handle recovery strategies
 */
export const audioPlayerErrorAtom = atom<AudioPlayerError | null>(
  initialErrorState
);
audioPlayerErrorAtom.debugLabel = 'audioPlayerErrorAtom';

/**
 * Track if audio is currently loading - used for loading indicators
 * Updates when audio is loading a new track
 */
export const audioLoadingAtom = atom<boolean>(false);
audioLoadingAtom.debugLabel = 'audioLoadingAtom';

/**
 * Track if audio has been initialized - helps with first play issues
 * This helps prevent autoplay restrictions issues
 */
export const audioInitializedAtom = atom<boolean>(false);
audioInitializedAtom.debugLabel = 'audioInitializedAtom';

/**
 * Flag to track when playback has been requested but the audio element isn't ready
 * Used to automatically start playback once the audio element is available
 */
export const awaitingPlaybackAtom = atom<boolean>(false);
awaitingPlaybackAtom.debugLabel = 'awaitingPlaybackAtom';

/**
 * Store pending seek position for track changes
 * When switching to a new track with a specific position, this stores the target
 * percentage so it can be applied immediately when the track loads, preventing
 * the "play then jump" glitch
 */
export const pendingSeekPositionAtom = atom<{
  trackId: number;
  percentage: number;
} | null>(null);
pendingSeekPositionAtom.debugLabel = 'pendingSeekPositionAtom';

/**
 * Create a write-only atom to update the audio element with logging
 * This ensures proper initialization/cleanup and logs events
 */
export const updateAudioElementAtom = atom(
  null,
  (get, set, audioElement: HTMLAudioElement | null) => {
    Sentry.addBreadcrumb({
      category: 'audio_player',
      message: `Audio element ${audioElement ? 'initialized' : 'removed'}`,
      level: 'info',
    });

    log('Setting audio element:', !!audioElement);
    set(audioElementAtom, audioElement);

    // Log audio element events for debugging
    if (audioElement) {
      const eventHandler = (event: Event) => {
        log(`Audio element event: ${event.type}`);
        Sentry.addBreadcrumb({
          category: 'audio_player',
          message: `Audio event: ${event.type}`,
          level: 'debug',
        });
      };

      audioElement.addEventListener('canplay', eventHandler);
      audioElement.addEventListener('playing', eventHandler);
      audioElement.addEventListener('pause', eventHandler);
      audioElement.addEventListener('error', (e) => {
        console.error('Audio error event:', e);
        Sentry.captureException(new Error(`Audio playback error: ${e}`), {
          tags: { component: 'AudioPlayer' },
          extra: { event: e },
        });
      });
    }
  }
);
updateAudioElementAtom.debugLabel = 'updateAudioElementAtom';

/**
 * Write-only atom to reset the entire audio player state
 * This clears all queues, resets playback state, and clears errors
 */
export const resetAudioPlayerAtom = atom(null, (get, set) => {
  Sentry.addBreadcrumb({
    category: 'audio_player',
    message: 'Resetting entire audio player state',
    level: 'info',
  });

  log('Resetting audio player state to initial values');

  // Reset all primary atoms to their initial states
  set(audioPlayerStateAtom, initialAudioPlayerState);
  set(playbackAtom, initialPlaybackState);
  set(audioPlayerErrorAtom, initialErrorState);
  set(audioLoadingAtom, false);
  set(audioInitializedAtom, false);
  set(awaitingPlaybackAtom, false);
  set(pendingSeekPositionAtom, null);

  // Clear search query states
  set(lastMusicQueryAtom, null);
  set(lastMusicFiltersAtom, null);
  set(lastSfxQueryAtom, null);
  set(lastSfxFiltersAtom, null);

  // Disconnect the per-element MediaElementAudioSourceNode if one exists.
  // audioContext + gainNode are intentionally NOT torn down — they outlive
  // resets and live for the page lifetime (per-tab AudioContext budget).
  const sourceNode = getMediaElementSource();
  if (sourceNode) {
    sourceNode.disconnect();
    setMediaElementSource(null);
  }

  // Stop audio playback if element exists
  const audioElement = get(audioElementAtom);
  if (audioElement) {
    audioElement.pause();
    audioElement.currentTime = 0;
  }
});
resetAudioPlayerAtom.debugLabel = 'resetAudioPlayerAtom';
